/**
 * /admin/legal-packet — attorney-ready legal-review documents.
 *
 * Read-only surface: lists the .md files in /app/memory/legal_prep/ (served
 * via GET /api/admin/legal-packet/list) and offers per-row + all-as-zip
 * downloads.
 *
 * Every document in the packet is marked "DRAFT — for attorney review".
 * The banner at the top of the page reiterates that so an admin can never
 * ship a file from here as final.
 */
import { useCallback, useEffect, useState } from "react";
import { api, API_ROOT } from "../lib/api";
import { formatBytes, absoluteTimestamp } from "./backupUtils";

export function LegalPacketPage() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ count: 0, totalSize: 0 });
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null);
  const [attorneyEmail, setAttorneyEmail] = useState("");
  const [savedAttorney, setSavedAttorney] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, attRes] = await Promise.all([
        api.get("/admin/legal-packet/list"),
        api.get("/admin/legal-packet/attorney-email"),
      ]);
      setItems(listRes.data.items || []);
      setMeta({ count: listRes.data.count || 0, totalSize: listRes.data.totalSize || 0 });
      const em = attRes.data.attorneyEmail || "";
      setSavedAttorney(em);
      setAttorneyEmail((prev) => prev || em);
      setBanner(null);
    } catch (e) {
      setBanner({
        kind: "err",
        text: `Failed to load packet: ${e?.response?.data?.detail || e.message}`,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validEmail = (s) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s || "");
  const dirty = attorneyEmail !== savedAttorney;

  const saveAttorney = async () => {
    if (!validEmail(attorneyEmail)) {
      setBanner({ kind: "err", text: "Enter a valid email address." });
      return;
    }
    setSavingEmail(true);
    try {
      const { data } = await api.put("/admin/legal-packet/attorney-email", { attorneyEmail });
      setSavedAttorney(data.attorneyEmail || "");
      setBanner({ kind: "ok", text: "Attorney email saved." });
    } catch (e) {
      setBanner({ kind: "err", text: `Save failed: ${e?.response?.data?.detail?.message || e.message}` });
    } finally {
      setSavingEmail(false);
    }
  };

  const sendPacket = async () => {
    if (!validEmail(attorneyEmail)) {
      setBanner({ kind: "err", text: "Enter a valid email address before sending." });
      return;
    }
    if (!window.confirm(
      `Send the legal packet zip to ${attorneyEmail}? This will attach ${meta.count} file(s) ` +
      `(${formatBytes(meta.totalSize)}) and log the send to the admin audit trail.`,
    )) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/legal-packet/email", {
        attorneyEmail,
        note: note.trim() || null,
      });
      setBanner({
        kind: "ok",
        text: `Sent ${data.fileCount} file(s) (${formatBytes(data.zipBytes)}) to ${attorneyEmail}. ` +
              `Remaining this hour: ${data.remainingThisHour}.`,
      });
      setNote("");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && (detail.message || detail.error)) || e.message;
      setBanner({ kind: "err", text: `Send failed: ${msg}` });
    } finally {
      setSending(false);
    }
  };

  const humanName = (filename) => {
    const base = filename.replace(/\.[^.]+$/, "");
    const map = {
      "README": "Attorney cover letter (README)",
      "coppa_compliance_audit": "COPPA Compliance Audit",
      "privacy_policy_v2_DRAFT": "Privacy Policy v2 (DRAFT)",
      "terms_of_service_v2_DRAFT": "Terms of Service v2 (DRAFT)",
      "trademark_note": "Trademark Note",
    };
    if (map[base]) return map[base];
    return base.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const zipHref = `${API_ROOT}/admin/legal-packet/download`;
  const fileHref = (name) => `${API_ROOT}/admin/legal-packet/download/${encodeURIComponent(name)}`;

  return (
    <section data-testid="admin-legal-packet" style={{ padding: "0 2rem 3rem" }}>
      <header style={{ marginBottom: "1.5rem" }}>
        <h1 style={{ fontSize: "2rem", margin: 0 }}>Legal Packet</h1>
        <p style={{ marginTop: 6, color: "#5c5470", maxWidth: 780, lineHeight: 1.5 }}>
          Attorney-ready documents for COPPA + trademark review. Every file
          in this packet is marked{" "}
          <strong>“DRAFT — for attorney review.”</strong>{" "}
          Refresh the packet by editing the files in{" "}
          <code>/app/memory/legal_prep/</code> — the list below auto-reflects
          whatever is on disk.
        </p>
      </header>

      <div
        role="note"
        data-testid="legal-packet-banner"
        style={{
          display: "flex", gap: 12, alignItems: "flex-start",
          padding: "0.85rem 1rem", marginBottom: "1.25rem",
          background: "#fff8e6", border: "2px solid #d99f00",
          borderRadius: 12, color: "#5a3d00", lineHeight: 1.45,
        }}
      >
        <span aria-hidden style={{ fontSize: 22, lineHeight: 1 }}>📎</span>
        <div style={{ flex: 1 }}>
          <strong>Not legal advice.</strong> These files are a first-pass
          engineering summary produced so counsel of record can start from
          facts, not a blank page. Nothing here is a final document. Do not
          publish, send to third parties, or rely on any wording without
          attorney sign-off.
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: "1rem", flexWrap: "wrap" }}>
        <a
          href={zipHref}
          data-testid="legal-packet-download-zip"
          className="admin-btn admin-btn--danger"
          style={{
            background: "#c33d3d", color: "white", border: "none",
            padding: "0.65rem 1.25rem", borderRadius: 10, fontWeight: 600,
            textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8,
            cursor: "pointer",
          }}
        >
          ⬇ Download all as ZIP
        </a>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          data-testid="legal-packet-refresh"
          style={{
            background: "white", color: "#333",
            border: "1.5px solid #cfc7de", padding: "0.6rem 1.1rem",
            borderRadius: 10, fontWeight: 500, cursor: loading ? "wait" : "pointer",
          }}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
        <div style={{ marginLeft: "auto", color: "#5c5470", fontSize: 14 }}>
          <span data-testid="legal-packet-count">{meta.count}</span>{" "}
          file{meta.count === 1 ? "" : "s"} · total{" "}
          <span data-testid="legal-packet-total-size">{formatBytes(meta.totalSize)}</span>
        </div>
      </div>

      {banner ? (
        <div
          style={{
            padding: "0.75rem 1rem", marginBottom: "1rem",
            background: banner.kind === "err" ? "#ffe6e6" : "#e6f7ea",
            border: `1.5px solid ${banner.kind === "err" ? "#c33d3d" : "#3ea55e"}`,
            borderRadius: 10, color: banner.kind === "err" ? "#8a1a1a" : "#1f5a2f",
          }}
        >
          {banner.text}
        </div>
      ) : null}

      <section
        data-testid="legal-packet-email-section"
        style={{
          background: "white", borderRadius: 14,
          boxShadow: "0 2px 12px rgba(60, 40, 100, 0.08)",
          padding: "1.25rem 1.5rem", marginBottom: "1.5rem",
        }}
      >
        <h2 style={{ margin: 0, fontSize: "1.15rem", color: "#3f2f5a" }}>Email packet to attorney</h2>
        <p style={{ marginTop: 6, marginBottom: "1rem", color: "#5c5470", fontSize: 14, lineHeight: 1.5 }}>
          Sends the ZIP as a SendGrid attachment with a cover message. Rate-limited to 5 sends per hour per admin.
          Every send is logged to the admin audit trail.
        </p>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
          <label htmlFor="attorney-email" style={{ fontWeight: 500, color: "#3f2f5a", minWidth: 170 }}>
            Attorney email address
          </label>
          <input
            id="attorney-email"
            type="email"
            data-testid="legal-packet-attorney-email"
            value={attorneyEmail}
            onChange={(e) => setAttorneyEmail(e.target.value)}
            placeholder="counsel@example.com"
            style={{
              flex: 1, minWidth: 260,
              padding: "0.55rem 0.85rem",
              border: `1.5px solid ${validEmail(attorneyEmail) || !attorneyEmail ? "#cfc7de" : "#c33d3d"}`,
              borderRadius: 8, fontSize: 14,
            }}
          />
          <button
            type="button"
            onClick={saveAttorney}
            disabled={!dirty || savingEmail || !validEmail(attorneyEmail)}
            data-testid="legal-packet-save-attorney"
            style={{
              padding: "0.55rem 1rem", borderRadius: 8, fontWeight: 500,
              border: "1.5px solid #cfc7de", background: "white",
              cursor: (dirty && validEmail(attorneyEmail) && !savingEmail) ? "pointer" : "not-allowed",
              opacity: (dirty && validEmail(attorneyEmail) && !savingEmail) ? 1 : 0.55,
            }}
          >
            {savingEmail ? "Saving…" : dirty ? "Save" : "Saved"}
          </button>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label htmlFor="attorney-note" style={{ display: "block", fontWeight: 500, color: "#3f2f5a", marginBottom: 6 }}>
            Optional note (added to the email body)
          </label>
          <textarea
            id="attorney-note"
            data-testid="legal-packet-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="e.g. Please prioritize the trademark clearance; owner would like to submit an ITU within 2 weeks."
            style={{
              width: "100%", padding: "0.6rem 0.85rem",
              border: "1.5px solid #cfc7de", borderRadius: 8,
              fontSize: 14, fontFamily: "inherit", resize: "vertical",
            }}
          />
        </div>
        <button
          type="button"
          onClick={sendPacket}
          disabled={sending || !validEmail(attorneyEmail) || meta.count === 0}
          data-testid="legal-packet-send"
          style={{
            background: "#c33d3d", color: "white", border: "none",
            padding: "0.7rem 1.35rem", borderRadius: 10, fontWeight: 600,
            cursor: sending ? "wait" : (validEmail(attorneyEmail) ? "pointer" : "not-allowed"),
            opacity: (validEmail(attorneyEmail) && !sending && meta.count > 0) ? 1 : 0.6,
          }}
        >
          {sending ? "Sending…" : "✉ Email packet to attorney"}
        </button>
        {savedAttorney ? (
          <p style={{ marginTop: 10, fontSize: 12, color: "#8a809e" }}>
            Currently saved: <code>{savedAttorney}</code>
          </p>
        ) : null}
      </section>

      <div style={{
        background: "white", borderRadius: 14,
        boxShadow: "0 2px 12px rgba(60, 40, 100, 0.08)",
        overflow: "hidden",
      }}>
        <table
          data-testid="legal-packet-table"
          style={{ width: "100%", borderCollapse: "collapse" }}
        >
          <thead style={{ background: "#f6f3fb", color: "#3f2f5a" }}>
            <tr>
              <th style={thStyle}>Document</th>
              <th style={thStyle}>Filename</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Size</th>
              <th style={thStyle}>Last modified</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading ? (
              <tr>
                <td colSpan={5} style={{ ...tdStyle, textAlign: "center", padding: "2rem", color: "#8a809e" }}>
                  Packet is empty. Add files to <code>/app/memory/legal_prep/</code> and refresh.
                </td>
              </tr>
            ) : null}
            {items.map((it) => (
              <tr key={it.filename} data-testid={`legal-packet-row-${it.filename}`}>
                <td style={tdStyle}>
                  <strong>{humanName(it.filename)}</strong>
                </td>
                <td style={{ ...tdStyle, fontFamily: "ui-monospace, monospace", fontSize: 13, color: "#5c5470" }}>
                  {it.filename}
                </td>
                <td style={{ ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                  {formatBytes(it.size)}
                </td>
                <td style={{ ...tdStyle, color: "#5c5470", fontSize: 13 }}>
                  {absoluteTimestamp(it.modifiedAt)}
                </td>
                <td style={{ ...tdStyle, textAlign: "right" }}>
                  <a
                    href={fileHref(it.filename)}
                    data-testid={`legal-packet-download-${it.filename}`}
                    style={{
                      display: "inline-block",
                      padding: "0.4rem 0.85rem",
                      background: "#4a4177", color: "white",
                      borderRadius: 8, textDecoration: "none",
                      fontSize: 14, fontWeight: 500,
                    }}
                  >
                    ⬇ Download
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p style={{ marginTop: "1.5rem", color: "#8a809e", fontSize: 13, maxWidth: 720 }}>
        Attorney handoff tip: download the ZIP, forward to counsel with a
        brief email describing the app&apos;s launch timeline, and ask them to
        annotate directly on the .md files (or convert to PDF/DOCX on
        their side). All markdown renders in GitHub, VS Code, Obsidian,
        and most modern editors.
      </p>
    </section>
  );
}

const thStyle = {
  textAlign: "left",
  padding: "0.85rem 1rem",
  fontWeight: 600,
  fontSize: 13,
  letterSpacing: "0.02em",
  textTransform: "uppercase",
  borderBottom: "1.5px solid #ece5f5",
};

const tdStyle = {
  padding: "0.85rem 1rem",
  borderBottom: "1px solid #f3eefa",
  verticalAlign: "middle",
};
